/** OTC 문서 컴포넌트(OtcDocVarField 등)가 패널의 공유 상태에 접근하는 provide/inject 컨텍스트.
 *  CDD(cddContext) 미러링. 단, OTC 는 문서 인스턴스가 아니라 "12문서 패키지 공유 values 맵" 이라
 *  activeDoc 개념 없이 하나의 values / flags / 반복부(allottees·shares) 를 공유한다. */
import type { InjectionKey, Ref } from 'vue';
import type { OtcFieldType } from './otcFields';
import type { ComboOpt } from '../cdd/cddOptions';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';

export interface OtcDocContext {
  valueOf: (key: string) => string;
  labelOf: (key: string) => string;
  typeOf: (key: string) => OtcFieldType;
  activeKey: Ref<string>;
  activate: (key: string) => void;              // 변수 클릭 → 패널이 챗봇/입력바 라우팅
  setFieldValue: (key: string, val: string) => void; // 드래그앤드롭·직접 채우기
  openAddress: (key: string) => void;           // 주소 필드 📍 → 주소 입력(모달/챗봇)
  comboOptionsOf: (key: string) => ComboOpt[];

  // ── 조건 토글([[if X]]) ──
  flag: (name: string) => boolean;
  setFlag: (name: string, val: boolean) => void;
  toggleFlag: (name: string) => void;
  /** 문자열 flag(resolution_basis: constitution|articles|third_schedule 등). */
  choiceOf: (name: string) => string;
  setChoice: (name: string, val: string) => void;

  // ── Governing Document (정관 결의 근거) ──
  /** entity 에 정관/clause 등록돼 있으면 true → clause/basis 편집 잠금(readonly). */
  clauseLocked: Ref<boolean>;
  /** 미등록 시 AI 선택용 clause 옵션(entity resolution-options). */
  clauseOptions: Ref<string[]>;
  /** clause 옵션/직접입력 선택 → constitution_clause_number 정규화 저장 + resolution_basis 자동 감지. */
  setClause: (raw: string) => void;

  // ── 반복부: Allottees ──
  allotteeCount: Ref<number>;
  addAllottee: () => void;
  removeAllottee: (index: number) => void;      // 1-based
  /** entity 활성 member(주주)를 Allottee 표에 일괄 로드(name/주소/contactCd/id). 주식수는 사용자 입력. */
  loadAllotteesFromMembers: () => void;
  /** member 로드 가능(entity 선택됨) 여부 + 진행중. */
  canLoadMembers: Ref<boolean>;
  loadingMembers: Ref<boolean>;

  // ── 반복부: Share classes (Section 78) ──
  shareClassCount: Ref<number>;
  addShareClass: () => void;
  removeShareClass: (index: number) => void;    // 1-based

  // ── Stage 2 청약자 유형 분기 (Individual ↔ Corporate) ──
  subscriberType: Ref<'individual' | 'corporate'>;
  setSubscriberType: (t: 'individual' | 'corporate') => void;

  // ── [[Signers]] — 문서별 서명자 선택(SignersField) ──
  entityCd: Ref<string>;                                  // Add Signers 모달의 entity 임원 조회용
  signersOf: (varKey: string) => SignerEntry[];           // varKey 별 선택된 서명자
  setSigners: (varKey: string, list: SignerEntry[]) => void;

  // ── 검증 — Create Task 시 누락 필드 빨간 하이라이트 ──
  isInvalid: (key: string) => boolean;

  // ── pick-place (챗봇 상세카드 copy → 필드 클릭 삽입) ──
  pendingPickValue: Ref<string>;
  placePick: (key: string) => boolean;
}

export const OTC_DOC_CTX: InjectionKey<OtcDocContext> = Symbol('otcDocCtx');
